import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

// Create a client with optimized settings for performance and deduplication
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes by default
      staleTime: 5 * 60 * 1000,
      // Keep data in cache for 10 minutes (React Query v5: gcTime)
      gcTime: 10 * 60 * 1000,
      // Retry failed requests 2 times, but not for AbortErrors or 404s
      retry: (failureCount, error) => {
        // Don't retry on AbortError (cancellations during hard refresh)
        if (error instanceof Error && error.name === "AbortError") {
          return false;
        }
        if (error instanceof Error) {
          const message = error.message.toLowerCase();
          if (
            message.includes("timeout") ||
            message.includes("taking longer than usual") ||
            message.includes("503") ||
            message.includes("524") ||
            message.includes("temporarily unavailable")
          ) {
            return false;
          }
        }
        // Don't retry on 404 errors (resource not found - won't exist on retry)
        if (
          (error instanceof Error && error.message?.includes("404")) ||
          (error instanceof Error && error.message?.includes("not found"))
        ) {
          return false;
        }
        return failureCount < 2;
      },
      // Don't refetch on window focus to reduce unnecessary requests
      refetchOnWindowFocus: false,
      // Refetch on mount only when mutations invalidate queries — avoids refetch storm on navigation.
      refetchOnMount: false,
      // Refetch stale queries when the browser reconnects after going offline
      refetchOnReconnect: true,
      // Enable structural sharing to prevent unnecessary re-renders
      structuralSharing: true,
      // Network mode: prefer cache during hard refresh to reduce cancellations
      networkMode: "online",
    },
    mutations: {
      // Retry mutations once, but not on AbortError
      retry: (failureCount, error) => {
        if (error instanceof Error && error.name === "AbortError") {
          return false;
        }
        return failureCount < 1;
      },
    },
  },
});

interface QueryProviderProps {
  children: React.ReactNode;
}

export function QueryProvider({ children }: Readonly<QueryProviderProps>) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* Only show devtools in development */}
      {process.env.NODE_ENV === "development" && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { queryClient };
